"use client";

import { useSyncExternalStore } from "react";
import { formatEditorialDate } from "@/components/editorial-system/editorial-primitives";

export function formatTimeAgo(iso: string, now = Date.now()) {
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return "";
	const minutes = Math.max(0, Math.round((now - then) / 60000));
	if (minutes < 1) return "now";
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.round(minutes / 60);
	if (hours < 24) return `${hours}hr ago`;
	return formatEditorialDate(iso);
}

/*
 * One clock for every TimeAgo on the page: a single interval, started by the
 * first label and stopped when the last one unmounts.
 */
const listeners = new Set<() => void>();
let timer: number | undefined;
let now = 0;

function subscribe(listener: () => void) {
	listeners.add(listener);
	if (timer === undefined) {
		timer = window.setInterval(() => {
			now = Date.now();
			for (const notify of listeners) notify();
		}, 60_000);
	}
	return () => {
		listeners.delete(listener);
		if (!listeners.size && timer !== undefined) {
			window.clearInterval(timer);
			timer = undefined;
		}
	};
}

function getNow() {
	// Read once and keep until the next tick, so React sees a stable value.
	if (!now) now = Date.now();
	return now;
}
/** On the server and during hydration there is no clock: show the date. */
const getServerNow = () => 0;

/**
 * "2hr ago" in the browser for the last 24 hours, then "26 OCT. 26".
 * Renders the plain date first so server output never depends on the time.
 */
export function TimeAgo({ iso }: { iso: string }) {
	const current = useSyncExternalStore(subscribe, getNow, getServerNow);
	return (
		<time dateTime={iso} suppressHydrationWarning>
			{current ? formatTimeAgo(iso, current) : formatEditorialDate(iso)}
		</time>
	);
}
